/* eslint-disable max-len */

import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as v4Link } from 'react-router-dom';
import { hideRegLoginLightbox, setRegLoginRedirectUrl, showRegLoginLightbox } from '@zillow/user-session-store';

import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import HeadWrapper from 'app/shared/components/HeadWrapper';
import { TextButton, Anchor } from '@zillow/constellation';

import { LabsterActions } from '@zg-rentals/particles-js-labster';

import Button from 'app/shared/components/LandingPageRefresh/Button';
import Carousel from 'app/shared/components/LandingPageRefresh/Carousel';
import Checklist from 'app/shared/components/LandingPageRefresh/Checklist';
import ContentWrapper from 'app/shared/components/LandingPageRefresh/ContentWrapper';
import CrossPromoContainer from 'app/shared/components/LandingPageRefresh/CrossPromoContainer';
import CrossPromoContent from 'app/shared/components/LandingPageRefresh/CrossPromoContent';
import Hero from 'app/shared/components/LandingPageRefresh/Hero';
import Image from 'app/shared/components/LandingPageRefresh/Image';
import ImageBackground from 'app/shared/components/LandingPageRefresh/ImageBackground';
import ImageContainer from 'app/shared/components/LandingPageRefresh/ImageContainer';
import ImageOverlay from 'app/shared/components/LandingPageRefresh/ImageOverlay';
import Link from 'app/shared/components/LandingPageRefresh/Link';
import MoreButton from 'app/shared/components/LandingPageRefresh/MoreButton';
import PromoBlock from 'app/shared/components/LandingPageRefresh/PromoBlock';
import ReviewContent from 'app/shared/components/LandingPageRefresh/ReviewContent';
import TextBlock from 'app/shared/components/LandingPageRefresh/TextBlock';
import TextContainer from 'app/shared/components/LandingPageRefresh/TextContainer';
import Title from 'app/shared/components/LandingPageRefresh/Title';
import Tooltip from 'app/shared/components/LandingPageRefresh/Tooltip';
import { DELIMITER } from 'app/shared/constants/AnalyticsDelimiterConstants';

import { RMWContext } from 'app/shared/providers/RMWContext';
import { UserSelectors } from 'app/shared/selectors';

import PostingSearchModule from 'app/shared/components/PostingSearchModule';
import Images from 'app/shared/images/listingLandingPageV2';
import * as S from './styles';

const pageTitle = 'List Your Rental Property, Post Apartments For Rent';
const pageMetaDescription = 'Post your first rental listing free on Zillow Rental Manager and ' +
    'quickly rent out your house, apartment or condo through the most visited rental network.';

const ListingLandingPage = () => {
    const dispatch = useDispatch();
    const { paths } = React.useContext(RMWContext);
    const isProd = useSelector(state => state.app.isRunningInProd);
    const isLoggedIn = useSelector(state => UserSelectors.makeIsLoggedIn(state));
    const isListingFormTeaserExp = dispatch(LabsterActions.isExperiment('listing-form-teaser'));

    useEffect(() => {
        window.scroll(0, 0);

        return () => {
            hideRegLoginLightbox();
        };
    }, []);

    const trackGaEvent = (eventName) => {
        switch (eventName) {
            case 'HeroCtaPostListingFree': {
                dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.POST_LISTING_GET_STARTED));
                break;
            }
            case `SignIn${DELIMITER}Hero`: {
                dispatch(analyticsUtils.track.GAEventV2({
                    category: `call_to_action${DELIMITER}text`,
                    label: 'ClickThrough',
                    action: `SignIn${DELIMITER}hero`,
                }));
                break;
            }
            case `GetRentZestimate${DELIMITER}Module3`: {
                dispatch(analyticsUtils.track.GAEventV2({
                    category: `call_to_action${DELIMITER}text`,
                    label: 'ClickThrough',
                    action: `GetRentZestimate${DELIMITER}Module3`,
                }));
                break;
            }
            case `LearnMoreScreening${DELIMITER}Module4`: {
                dispatch(analyticsUtils.track.GAEventV2({
                    category: `call_to_action${DELIMITER}text`,
                    label: 'ClickThrough',
                    action: `LearnMoreScreening${DELIMITER}Module4`,
                }));
                break;
            }
            case `GetStarted${DELIMITER}Module6`: {
                dispatch(analyticsUtils.track.GAEventV2({
                    category: `call_to_action${DELIMITER}button`,
                    label: 'ClickThrough',
                    action: `GetStarted${DELIMITER}Module6`,
                }));
                break;
            }
            case `LearnZillowRentalManager${DELIMITER}Module7`: {
                dispatch(analyticsUtils.track.GAEventV2({
                    category: `call_to_action${DELIMITER}text`,
                    label: 'ClickThrough',
                    action: `LearnZillowRentalManager${DELIMITER}Module7`,
                }));
                break;
            }
            case `GetStarted${DELIMITER}BottomModule`: {
                dispatch(analyticsUtils.track.GAEventV2({
                    category: `call_to_action${DELIMITER}button`,
                    label: 'ClickThrough',
                    action: `GetStarted${DELIMITER}BottomModule`,
                }));
                break;
            }
            default: {
                console.warn('No case for trackingGaEvent');
                break;
            }
        }
    };

    const handleSignInClick = (e) => {
        e.preventDefault();
        showRegLoginLightbox();

        trackGaEvent(`SignIn${DELIMITER}Hero`);
    };

    const handleNavigationToSensitivePage = ({ e, eventName, url }) => {
        // track event
        trackGaEvent(eventName);

        if (!isLoggedIn) {
            // block navigation
            e.preventDefault();

            // set redirectUrl for login success
            setRegLoginRedirectUrl(url);

            // open login modal
            showRegLoginLightbox();
        }
    };

    return (
        <S.ListingLandingPage noPadding={true} className="ListingLandingPage">
            <HeadWrapper
                title={pageTitle}
                description={pageMetaDescription}
            />
            <Hero.Wrapper backgroundImage={Images.HeroImage} alignItems={isListingFormTeaserExp ? 'flex-start' : ''}>
                {isListingFormTeaserExp ? (
                    <PostingSearchModule />
                ) : (
                    <Hero.Content>
                        <Title.H1>
                        It&apos;s quick and easy to post a rental listing
                        </Title.H1>
                        <S.HeroSubtextMaxWidth>
                            <Hero.Subtext>
                            Fill your vacancy on the network with over 1 million daily visitors.
                                {' '}
                            Post your first listing free today.
                            </Hero.Subtext>
                        </S.HeroSubtextMaxWidth>
                        <Link.PromoLink
                            reactRouterLink={v4Link}
                            to={`${paths.propertiesPage}?postingPath=true`}
                        >
                            <Button.Hero>List your rental</Button.Hero>
                        </Link.PromoLink>
                        {!isLoggedIn && (
                            <Hero.HasAccount>
                                Already have an account?
                                {' '}
                                <Anchor
                                    as="a"
                                    onClick={handleSignInClick}
                                    inverse={true}
                                >
                                    <strong>Sign in</strong>
                                </Anchor>
                            </Hero.HasAccount>
                        )}
                    </Hero.Content>
                )}
                <MoreButton />
            </Hero.Wrapper>
            <PromoBlock forceAlign="center">
                <Title.H2>
                    Post where renters are searching
                </Title.H2>
                <TextBlock forceAlign="center" marginTop="20px" padding="0 50px" maxWidth="700px">
                    Gain access to the network with over 34 million visitors each month —
                    {' '}
                    and get more eyes on your listings.
                </TextBlock>
            </PromoBlock>

            <PromoBlock>
                <ContentWrapper>
                    <TextContainer>
                        <Title.H3>
                            The hassle-free way to find renters
                        </Title.H3>
                        <TextBlock>
                            List your house, townhome, condo or apartment for rent on Zillow Rental Manager
                            {' '}
                            and it will apear on three of the most visited rental sites.
                        </TextBlock>
                        <S.PromoLogos>
                            <Image src={Images.ZillowLogo} src2x={Images.ZillowLogo2x} alt="Zillow logo" />
                            <Image src={Images.TruliaLogo} src2x={Images.TruliaLogo2x} alt="Trulio logo" />
                            <Image src={Images.HotPadsLogo} src2x={Images.HotPadsLogo2x} alt="HotPads logo" />
                        </S.PromoLogos>
                    </TextContainer>
                    <ImageContainer>
                        <ImageOverlay align="left">
                            <Image
                                src={Images.Block2Overlay}
                                src2x={Images.Block2Overlay2x}
                                alt="Screenshot showing a map with properties listed."
                            />
                        </ImageOverlay>
                        <ImageBackground
                            extraSmall={Images.Block2Overlay}
                            extraSmall2x={Images.Block2Overlay2x}
                            small={Images.Block2OverlayWide}
                            small2x={Images.Block2OverlayWide2x}
                            medium={Images.Block2OverlayWide}
                            medium2x={Images.Block2OverlayWide2x}
                            extraLarge={Images.Block2Image}
                            extraLarge2x={Images.Block2Image2x}
                        />
                    </ImageContainer>
                </ContentWrapper>
            </PromoBlock>

            <PromoBlock>
                <ContentWrapper>
                    <ImageContainer>
                        <ImageOverlay align="right">
                            <Image
                                src={Images.Block3Overlay}
                                src2x={Images.Block3Overlay2x}
                                alt="Screenshot showing a direct message conversation
                                between the landlord and the renter."
                            />
                        </ImageOverlay>
                        <ImageBackground
                            extraSmall={Images.Block3Overlay}
                            extraSmall2x={Images.Block3Overlay2x}
                            small={Images.Block3OverlayWide}
                            small2x={Images.Block3OverlayWide2x}
                            medium={Images.Block3OverlayWide}
                            medium2x={Images.Block3OverlayWide2x}
                            extraLarge={Images.Block3Image}
                            extraLarge2x={Images.Block3Image2x}
                        />
                    </ImageContainer>
                    <TextContainer>
                        <Title.H3>
                            Listing made simple
                        </Title.H3>
                        <TextBlock>
                            <b>Create your rental listings</b>
                            <br />
                            It takes just minutes to create a listing &#8212; simply add property details,
                            {' '}
                            upload photos and publish!
                        </TextBlock>
                        <TextBlock>
                            <b>Message interested renters</b>
                            <br />
                            Communicate with renters using Zillow Rental Manager to stay organized during the
                            {' '}
                            tenant search process.
                        </TextBlock>
                        <TextBlock>
                            <b>Reuse your rental posting</b>
                            <br />
                            Time for new renters? We save your listings so you can republish
                            {' '}
                            them with the click of a button.
                        </TextBlock>
                    </TextContainer>
                </ContentWrapper>
            </PromoBlock>
            <PromoBlock>
                <ContentWrapper>
                    <TextContainer>
                        <Title.H3>
                            Price your rental
                        </Title.H3>
                        <TextBlock>
                            Feel confident when you list your property &#8212; we&apos;ll help
                            {' '}
                            you price it by integrating your Rent Zestimate into the listing and
                            {' '}
                            giving you tools to review comparable properties.
                        </TextBlock>
                        <TextBlock>
                            <TextButton
                                as="a"
                                onClick={() => trackGaEvent(`GetRentZestimate${DELIMITER}Module3`)}
                                href="https://www.zillow.com/rental-manager/price-my-rental/"
                            >
                                Get your Rent Zestimate
                            </TextButton>
                        </TextBlock>
                    </TextContainer>
                    <ImageContainer>
                        <ImageOverlay align="left">
                            <Image
                                src={Images.Block4Overlay}
                                src2x={Images.Block4Overlay2x}
                                alt="Screenshot of Rent Zestimate history comparing this home,
                                the neighborhood, and the city"
                            />
                        </ImageOverlay>
                        <ImageBackground
                            extraSmall={Images.Block4Overlay}
                            extraSmall2x={Images.Block4Overlay2x}
                            small={Images.Block4OverlayWide}
                            small2x={Images.Block4OverlayWide2x}
                            medium={Images.Block4OverlayWide}
                            medium2x={Images.Block4OverlayWide2x}
                            extraLarge={Images.Block4Image}
                            extraLarge2x={Images.Block4Image2x}
                        />
                    </ImageContainer>
                </ContentWrapper>
            </PromoBlock>
            <PromoBlock>
                <ContentWrapper>
                    <ImageContainer>
                        <ImageOverlay align="right">
                            <Image
                                src={Images.Block5Overlay}
                                src2x={Images.Block5Overlay2x}
                                alt="Screenshot of a background check. Showing Name, last four digits of their
                                    social security number, Date of birth, email, and the status of SSN trace,
                                    eviction search, sex offender search, Global watchlist search, and national
                                    criminal search."
                            />
                        </ImageOverlay>
                        <ImageBackground
                            extraSmall={Images.Block5Overlay}
                            extraSmall2x={Images.Block5Overlay2x}
                            small={Images.Block5OverlayWide}
                            small2x={Images.Block5OverlayWide2x}
                            medium={Images.Block5OverlayWide}
                            medium2x={Images.Block5OverlayWide2x}
                            extraLarge={Images.Block5Image}
                            extraLarge2x={Images.Block5Image2x}
                        />
                    </ImageContainer>
                    <TextContainer>
                        <Title.H3>
                            Built-in tenant screening
                        </Title.H3>
                        <TextBlock>
                            When you list your property with Zillow Rental Manager, you can accept rental
                            {' '}
                            applications for free to quickly screen propsective tenants with background
                            {' '}
                            checks, credit reports and eviction history.
                            <Tooltip>
                                In some cases, application components, such as background checks or credit reports,
                                {' '}
                                are modified to reflect state and local legal requirements. See more information
                                {' '}
                                <Link.TooltipLink
                                    to="https://www.zillow.com/info/limits-rental-applications-reports/"
                                >
                                    here
                                </Link.TooltipLink>
                            </Tooltip>
                        </TextBlock>
                        <TextBlock>
                            <TextButton
                                as="a"
                                onClick={() => trackGaEvent(`LearnMoreScreening${DELIMITER}Module4`)}
                                href="https://www.zillow.com/z/rental-manager/tenant-screening/"
                            >
                                Learn more about screening
                            </TextButton>
                        </TextBlock>
                    </TextContainer>
                </ContentWrapper>
            </PromoBlock>
            <PromoBlock backgroundColor="#F9F9F9">
                <Title.H2>
                    Post a listing in 3 easy steps
                </Title.H2>
                <CrossPromoContainer margin="40px auto">
                    <CrossPromoContent>
                        <Image
                            alt="Screenshot showing adding listing details."
                            src={Images.Block6StepOne}
                            src2x={Images.Block6StepOne2x}
                            style={{ paddingBottom: '30px' }}
                        />
                        <Title.H4 forceAlign="center" padding="0 54px">1. Add property details and images</Title.H4>
                    </CrossPromoContent>
                    <CrossPromoContent>
                        <Image
                            alt="Screenshot showing rental price and date available being set."
                            src={Images.Block6StepTwo}
                            src2x={Images.Block6StepTwo2x}
                            style={{ paddingBottom: '30px' }}
                        />
                        <Title.H4 forceAlign="center" padding="0 54px">2. Set your monthly rent price</Title.H4>
                    </CrossPromoContent>
                    <CrossPromoContent>
                        <Image
                            src={Images.Block6StepThree}
                            src2x={Images.Block6StepThree2x}
                            alt="Screenshot showing an active listing."
                            style={{ paddingBottom: '30px' }}
                        />
                        <Title.H4 forceAlign="center" padding="0 54px">3. Publish and share with millions of renters</Title.H4>
                    </CrossPromoContent>
                </CrossPromoContainer>
            </PromoBlock>
            <PromoBlock>
                <ContentWrapper>
                    <TextContainer>
                        <Title.H3>
                            Your first listing is free
                        </Title.H3>
                        <TextBlock>
                            <Title.H4>Every listing includes:</Title.H4>
                            <div>
                                <Checklist>Posting to Zillow, Trulia and HotPads</Checklist>
                                <Checklist>
                                    Tenant screening with background and credit checks
                                    {' '}
                                    <Tooltip>
                                        In some cases, application components, such as background checks or credit reports, are modified to reflect state and local legal requirements. See more information
                                        {' '}
                                        <Link.TooltipLink
                                            to="https://www.zillow.com/info/limits-rental-applications-reports/"
                                        >
                                            here
                                        </Link.TooltipLink>
                                    </Tooltip>
                                </Checklist>
                                <Checklist>
                                    Online leases
                                    {' '}
                                    <Tooltip>
                                        Lease builder currently available in Colorado, Florida, Oregon, and select portions of Illinois. Excludes Cook County, Chicago, Aurora, Joliet, Evanston, Mount Prospect, Oak Park, and Elgin.
                                    </Tooltip>
                                </Checklist>
                                <Checklist>Online rent payments</Checklist>
                            </div>
                            <S.PaidListingMessage>
                                <span><i>Additional active listings are $9.99/week and are backed by our Lead Guarantee.</i></span>
                                {' '}
                                <Tooltip>
                                    If you don&apos;t receive a lead within a week, it&apos;s free to list until you do (for up to 8 weeks).
                                </Tooltip>
                            </S.PaidListingMessage>
                        </TextBlock>
                        <TextBlock>
                            <Link.PromoLink
                                onClick={e => handleNavigationToSensitivePage({
                                    e,
                                    eventName: `GetStarted${DELIMITER}Module6`,
                                    url: `${paths.propertiesPage}?postingPath=true`,
                                })}
                                reactRouterLink={v4Link}
                                to={`${paths.propertiesPage}?postingPath=true`}
                            >
                                <Button.Primary>Get Started</Button.Primary>
                            </Link.PromoLink>
                        </TextBlock>
                    </TextContainer>
                    <ImageContainer>
                        <Image src={Images.Block7Image} src2x={Images.Block7Image2x} alt="Placeholder" />
                    </ImageContainer>
                </ContentWrapper>
            </PromoBlock>
            <PromoBlock>
                <Title.H2>
                    Here&apos;s what others are saying
                </Title.H2>
                <Carousel>
                    <ReviewContent.Wrapper>
                        <ReviewContent.Body>
                            &quot;First time using Zillow Rental Manager, and I got more responses than any other ad I placed. Great comps for me to check out the value of my rental. Easy to use. I can&apos;t say enough good things about Zillow Rental Manager.&quot;
                        </ReviewContent.Body>
                        <ReviewContent.Byline>
                            Landlord, California, 1 unit
                        </ReviewContent.Byline>
                    </ReviewContent.Wrapper>
                    <ReviewContent.Wrapper>
                        <ReviewContent.Body>
                            &quot;Huge response: Ten showings, two fully-qualified prospects and rented within five days.&quot;
                        </ReviewContent.Body>
                        <ReviewContent.Byline>
                            Landlord, Georgia, 4 units
                        </ReviewContent.Byline>
                    </ReviewContent.Wrapper>
                    <ReviewContent.Wrapper>
                        <ReviewContent.Body>
                            &quot;It is amazing how easy it was to find a good tenant using Zillow Rental Manager! I really appreciate having this tool.&quot;
                        </ReviewContent.Body>
                        <ReviewContent.Byline>
                            Landlord, Florida, 1 unit
                        </ReviewContent.Byline>
                    </ReviewContent.Wrapper>
                    <ReviewContent.Wrapper>
                        <ReviewContent.Body>
                            &quot;Zillow Rental Manager makes it easy not only to advertise my listings but to manage the leads for those listings!&quot;
                        </ReviewContent.Body>
                        <ReviewContent.Byline>
                            Landlord, Texas, 7 units
                        </ReviewContent.Byline>
                    </ReviewContent.Wrapper>
                </Carousel>
            </PromoBlock>
            <PromoBlock backgroundColor="#F9F9F9">
                <Title.H2>
                    You can do more with Zillow Rental Manager
                </Title.H2>
                <CrossPromoContainer margin="40px auto">
                    <CrossPromoContent border={true} height="264px">
                        <Image
                            alt=""
                            src={Images.CrossPromoteOne}
                            src2x={Images.CrossPromoteOne2x}
                            style={{ paddingBottom: '25px' }}
                        />
                        <Title.H4 color="#006AFF" forceAlign="center">
                            <TextButton
                                as="a"
                                href="https://www.zillow.com/z/rental-manager/tenant-screening/"
                            >
                                Screen tenants
                            </TextButton>
                        </Title.H4>
                    </CrossPromoContent>
                    <CrossPromoContent border={true} height="264px">
                        <Image
                            alt=""
                            src={Images.CrossPromoteTwo}
                            src2x={Images.CrossPromoteTwo2x}
                            style={{ paddingBottom: '25px' }}
                        />
                        <Title.H4 color="#006AFF" forceAlign="center">
                            <TextButton
                                as="a"
                                href="https://www.zillow.com/z/rental-manager/rental-lease-agreements/"
                            >
                                Sign a lease
                            </TextButton>
                        </Title.H4>
                    </CrossPromoContent>
                    <CrossPromoContent border={true} height="264px">
                        <Image
                            alt=""
                            src={Images.CrossPromoteThree}
                            src2x={Images.CrossPromoteThree2x}
                            style={{ paddingBottom: '25px' }}
                        />
                        <Title.H4 color="#006AFF" forceAlign="center">
                            <TextButton
                                as="a"
                                href="https://www.zillow.com/z/rental-manager/collect-rent-online/"
                            >
                                Collect rent
                            </TextButton>
                        </Title.H4>
                    </CrossPromoContent>
                </CrossPromoContainer>
                <S.CrossPromoPadding>
                    <Title.H4 color="#006AFF" forceAlign="center">
                        <TextButton
                            onClick={() => trackGaEvent(`LearnZillowRentalManager${DELIMITER}Module7`)}
                            as="a"
                            href={isProd ? 'https://www.zillow.com/rental-manager/' : paths.homePage}
                        >
                            Learn about Zillow Rental Manager

                        </TextButton>
                    </Title.H4>
                </S.CrossPromoPadding>
            </PromoBlock>
            <Hero.Wrapper backgroundImage={Images.CtaBackground} height="400px">
                <Hero.Content>
                    <Title.H2 color="#fff">List your first rental property for free</Title.H2>
                    <S.HeroSubtextMaxWidth>
                        <TextBlock
                            forceAlign="center"
                            color="#fff"
                            marginTop="20px"
                        >
                            <Hero.Subtext>
                                Whether you manage one property or many, we have the tools
                                {' '}
                                to help you find your next renter.
                            </Hero.Subtext>
                            <Link.PromoLink
                                onClick={e => handleNavigationToSensitivePage({
                                    e,
                                    eventName: `GetStarted${DELIMITER}BottomModule`,
                                    url: `${paths.propertiesPage}?postingPath=true`,
                                })}
                                reactRouterLink={v4Link}
                                to={`${paths.propertiesPage}?postingPath=true`}
                            >
                                <Button.Cta>Get started</Button.Cta>
                            </Link.PromoLink>
                        </TextBlock>
                    </S.HeroSubtextMaxWidth>
                </Hero.Content>
            </Hero.Wrapper>
        </S.ListingLandingPage>
    );
};

export { ListingLandingPage };
export default ListingLandingPage;
